export default class TextGen {

    constructor(charsPerLine = 32) {
        this.charsPerLine = charsPerLine
    }

    /**
     * Convert encoded lines into text
     *
     * [L] - Align left, [C] - Align center, [R] - Align right (splits the line into equal columns).
     * [B] - Replace with one space. In case of text overflow break into 2 lines (both left aligned).
     * [S] - Fill out with spaces. In case of text overflow break into 2 lines (one left one right aligned).
     *
     * Examples:
     *
     * [L]left[C]center[R]right
     * [L]left[C]center[C]center[R]right
     * [L]text separated by[S]spaces
     * [L]text separated by[S]spaces overflow
     * [L]text with space[B]or break
     * [L]text with space[B]or break overflow
     * [C]overflowing text for which we create multiple lines and align all of them accordingly
     * [H11]text Highlight text - bold
     * [H12]text Highlight text - double height
     * [H22]text Highlight text - double width and height
     * [H00]text Highlight text - dense width and height
     * [HR] - line delimiter (equals to [L][D-])
     *
     * @param {string} text encoded line text
     * @param {number} charsPerLine num of characters per line
     * @param {string} delimiter fill text with spaces or another delimiter
     * @returns {string} generated text
     */
    line(text, charsPerLine = this.charsPerLine, delimiter = ' ') {
        // ignore highlight when converting to text
        text = text.replace('[H12]','').replace('[H22]','').replace('[H00]','').replace('[H11]','');

        if(text === '[HR]') {
            text = '[L][D-]';
        }

        const customDelimiterMatch = text.match(/\[D.\]/)
        if(customDelimiterMatch) {
            delimiter = customDelimiterMatch[0][2]
            text = text.replace(/\[D.\]/g, '')
        }

        let colNum = (text.match(/\[L\]/g) || []).length + (text.match(/\[C\]/g) || []).length + (text.match(/\[R\]/g) || []).length

        const colLength = Math.round(charsPerLine / colNum)
        const lastColLength = charsPerLine - colLength * (colNum - 1)

        let colContents = text.replace(/\[L\]/g, '[COL]').replace(/\[C\]/g, '[COL]').replace(/\[R\]/g, '[COL]').split('[COL]').slice(1, 5)

        const cols = []
        colContents.forEach(content => {
            const parsedChars = cols.join('').length
            cols.push(text.slice(parsedChars, parsedChars + 3) + content)
        })

        let result = ''
        cols.forEach((content, index) => {
            const length = index === cols.length - 1 ? lastColLength : colLength
            result += this.genText(content, length, cols.length === 1, delimiter).join('\n')
        })

        return result
    }

    genText(line, width, allowMultipleLines = true, delimiter = ' ') {
        let alignment = line.slice(0, 3)
        let textContent = line.slice(3)
        let fillSpaces = textContent.includes('[S]')
        let spaceOrBreak = textContent.includes('[B]')

        const spacesLeft = width - (textContent.length - (fillSpaces ? 3 : 0) - (spaceOrBreak ? 3 : 0))

        if(spaceOrBreak) {
            if(spacesLeft >= 1) {
                return [textContent.replace('[B]', ' ') + Array(spacesLeft - 1).fill(delimiter).join('')]
            }

            if(!allowMultipleLines) {
                return [textContent.replace('[B]', ' ')]
            }

            const segments = textContent.split('[B]')
            return [...this.genText('[L]' + segments[0], width), ...this.genText('[L]' + segments[1], width)]
        }

        if(fillSpaces) {
            if(spacesLeft >= 1) {
                return [textContent.replace('[S]', Array(spacesLeft).fill(delimiter).join(''))]
            }

            if(!allowMultipleLines) {
                return [textContent.replace('[S]', ' ')]
            }

            const segments = textContent.split('[S]')
            return [...this.genText('[L]' + segments[0], width), ...this.genText('[R]' + segments[1], width)]
        }

        if(spacesLeft >= 0 || !allowMultipleLines) {
            const spaces = spacesLeft > 0 ? Array(spacesLeft).fill(delimiter).join('') : ''
            const halfSpaces = spacesLeft > 0 ? Array(Math.floor(spacesLeft / 2)).fill(delimiter).join('') : ''
            const leftSpaces = alignment === '[R]' ? spaces : (alignment === '[C]' ? halfSpaces : '')
            const rightSpaces = alignment === '[L]' ? spaces : (alignment === '[C]' ? halfSpaces + (spacesLeft % 2 === 1 ? delimiter : '') : '')

            return [leftSpaces + textContent + rightSpaces]
        }

        let result = []
        let curLine = ''

        textContent
            .split(' ')
            .flatMap(word => {
                const splitWordReg = new RegExp(`.{1,${width}}`, 'g');
                return word.length <= width ? [word] : word.match(splitWordReg)
            }).forEach((word, index, array) => {
                if(!!word) {
                    if(curLine.length + word.length >= width) {
                        const curLineParsed = curLine !== '' ? this.genText(alignment + curLine, width) : []
                        result = [...result, ...curLineParsed]
                        curLine = word
                    } else {
                        curLine += (curLine.length > 0 ? ' ' : '') + word
                    }

                    if(index === array.length - 1) {
                        result = [...result, ...this.genText(alignment + curLine, width)]
                    }
                }
            })

        return result
    }

    linesToText(lines) {
        return lines.map(line => this.line(line)).join('\n')
    }
}
